/*
 * Copyright (c) 2024-2025. Haodong JU
 */

import { VTextField } from 'vuetify/lib/components'
import IKUtils from 'innerken-js-utils'
import i18n from '@/i18n'

/**
 * Default form field configuration
 *
 * @property {string} name - Display name of the field
 * @property {string} key - Property path in the model (can use dot notation for nested properties)
 * @property {Component} component - Vue component to render the field
 * @property {boolean} required - Whether the field is required
 * @property {*} default - Default value for the field
 * @property {string} hint - Help text to display below the field
 * @property {Function} shouldShow - Function that determines if the field should be displayed
 * @property {Function} onChanged - Function called when the field value changes
 * @property {Array} dependsOn - Array of field keys that this field depends on
 * @property {Object} preconditions - Object with field keys and expected values or validation functions
 *                                   Example: { 'benefitType': 'RULE_BASED', 'trigger.triggerType': 'WEEKLY' }
 *                                   Example with function: { 'benefitType': (value) => value === 'RULE_BASED' }
 * @property {string} tag - Tag for grouping fields (if not specified, will use 'required' or 'optional' based on required property)
 * @property {Object} componentProps - Props to pass to the component
 */
export const formField = {
  name: '',
  key: '',
  component: VTextField,
  required: true,
  default: null,
  hint: '',
  shouldShow: (model) => true,
  onChanged: (model) => {
  },
  dependsOn: [], // Array of field keys that this field depends on
  preconditions: null, // Object with field keys and expected values or validation functions
  componentProps: {
    rules: [],
    outlined: true,
    hideDetails: false,
    dense: true
  }

}
const ruleNoEmpty = (v) => {
  return v === false || v === 0 || !!v || i18n.t('cantBeEmpty')
}

/**
 * @param {name:string,key:string,component:object,required:boolean?} schema
 * @return {name:string,key:string,component:object}
 */
export function mapSchemaToField (schema) {
  const safeDefault = IKUtils.deepCopy(formField)
  const tmp = Object.assign({}, safeDefault, IKUtils.deepCopy(schema))
  if (!tmp.name) {
    tmp.name = tmp.key
  }
  tmp.componentProps = Object.assign({},
    safeDefault.componentProps,
    tmp.componentProps,
    { placeholder: i18n.t('PleaseInputTheX', { configItem: i18n.t(tmp.name) }) })
  tmp.required = tmp.required ?? true
  if (tmp.required) {
    if (tmp.componentProps.rules.length === 0) {
      tmp.componentProps.rules.push(ruleNoEmpty)
    }
  }

  // Always set tag based on required property, ignoring any custom tags
  tmp.tag = tmp.required ? 'required' : 'optional'

  // Ensure dependsOn is an array
  if (tmp.dependsOn && !Array.isArray(tmp.dependsOn)) {
    tmp.dependsOn = [tmp.dependsOn]
  }

  // If preconditions is provided but not an object, convert it to an object
  if (tmp.preconditions && typeof tmp.preconditions !== 'object') {
    console.warn(`Invalid preconditions for field ${tmp.key}. Preconditions should be an object.`)
    tmp.preconditions = null
  }
  return tmp
}
